/**
 * ItemPreview - Collapsed view of a menu item card
 * Shows image/video, title, price, and action buttons
 */
import { motion } from 'framer-motion';
import { Plus, ChevronDown, ChevronUp, ZoomIn } from 'lucide-react';

const popAnimation = {
  initial: { scale: 1 },
  pop: { 
    scale: [1, 1.2, 0.9, 1.1, 1],
    transition: { duration: 0.4, ease: "easeInOut" }
  }
};

export default function ItemPreview({
  item,
  isExpanded,
  setIsExpanded,
  hasCustomizations,
  formatPriceRange,
  videoRef,
  isAddingToCart,
  handleAddAsIs,
  setShowImageZoom
}) {
  return (
    <div className="cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
      {/* Image/Video Section */}
      <div className="relative aspect-[4/3] overflow-hidden bg-black group/image">
        {item.video ? (
          <video
            ref={videoRef}
            src={item.video}
            className="w-full h-full object-contain"
            muted
            loop
            playsInline
            preload="auto"
            data-testid={`video-${item.id}`}
          />
        ) : item.image ? (
          <>
            <img 
              src={item.image} 
              alt={item.name}
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              width="400"
              height="300"
              className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
            />
            <button
              onClick={(e) => { e.stopPropagation(); setShowImageZoom(true); }}
              className="absolute bottom-3 right-3 p-2 bg-black/60 hover:bg-black/80 rounded-full text-white opacity-0 group-hover/image:opacity-100 transition-opacity duration-200 z-10"
              aria-label="Zoom image"
              data-testid={`zoom-image-${item.id}`}
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </>
        ) : null}
        
        {/* Badges */}
        {item.is_bestseller && (
          <span className="absolute top-3 left-3 px-3 py-1.5 bg-accent text-white text-sm font-medium rounded-full">
            Bestseller
          </span>
        )}
        {item.is_featured && !item.is_bestseller && (
          <span className="absolute top-3 left-3 px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-full">
            Featured
          </span>
        )}
        {hasCustomizations && !item.skip_customization && !item.is_membership && !item.name?.toLowerCase().includes('coffee pass') && (
          <span className="absolute top-3 right-3 px-3 py-1.5 bg-blue-900 text-white text-sm font-medium rounded-full backdrop-blur-sm">
            Tap to customize
          </span>
        )}
      </div>
      
      {/* Title and Price */}
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-heading text-base sm:text-lg md:text-xl pr-2 line-clamp-2">{item.name}</h3>
          <span className="font-bold text-primary text-base sm:text-lg whitespace-nowrap">
            {formatPriceRange()}
          </span>
        </div>
        <p className="text-base text-muted-foreground mb-3">{item.description}</p>
        
        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-medium text-sm transition-all ${
              isExpanded 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-primary/10 text-primary hover:bg-primary/20'
            }`}
            data-testid={`customize-btn-${item.id}`}
          >
            {isExpanded ? <><ChevronUp className="w-4 h-4" />Close</> : <><ChevronDown className="w-4 h-4" />Customize</>}
          </button>
          
          {!isExpanded && (
            <motion.button
              onClick={handleAddAsIs}
              animate={isAddingToCart ? popAnimation.pop : popAnimation.initial}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-medium text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-md hover:shadow-lg"
              data-testid={`add-as-is-btn-${item.id}`}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add As-Is</span>
              <span className="sm:hidden">Add</span>
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
}
